// Central content for the HepCURE mockup site.

export const PUBLICATIONS = [
  {
    year: "2022",
    venue: "INHSU 2022",
    title:
      "HepCURE HCV model of care: successes and learnings from our collective efforts in screening and linkage to care during the COVID-19 pandemic",
    url: "https://inhsu.org/resource/hepcure-hcv-model-of-care-successes-and-learnings-from-our-collective-efforts-in-screening-and-linkage-to-care-activities-during-the-covid-19-pandemic/",
  },
  {
    year: "2023",
    venue: "INHSU 2023",
    title:
      "Feasibility evaluation of a national elimination model of care in Canada: implementing HepCURE as a comprehensive HCV screening and treatment program in Vancouver",
    url: "https://inhsu.org/resource/feasibility-evaluation-of-a-national-elimination-model-of-care-in-canada-successes-and-learnings-from-implementing-hepcure-as-a-comprehensive-hcv-screening-and-treatment-program-in-vancouver/",
  },
  {
    year: "2023",
    venue: "INHSU 2023",
    title:
      "Real-world implementation of point-of-care (POC) HCV RNA testing in a mobile unit in homeless encampments and shelters in Ontario, Canada",
    url: "https://inhsu.org/resource/real-world-implementation-of-point-of-care-poc-hcv-rna-testing-in-a-mobile-unit-in-homeless-encampments-and-shelters-in-ontario-canada-learnings-and-considerations-for-community-based-hcv-programs/",
  },
];

export const CONTACT = {
  phone: "1-844-925-CURE (2873)",
  phoneHref: "tel:1-844-925-2873",
  email: "info@hepcure.ca",
  address: "530 Adelaide St. W., Unit 6107",
  city: "Toronto, ON M5V 1T5",
  hours: "By appointment only",
  appointment: "Register online or call to schedule",
};

export const IMAGES = {
  logo: "/brand/hepcure-logo.png",
  teamPhoto: "/brand/team-mobile-unit.jpg",
  hero: "/brand/hero.jpg",
  mobileUnitSummer: "/brand/mobile-unit-summer.jpg",
  mobileUnitWinter: "/brand/mobile-unit-winter.jpg",
  heroCaring:
    "https://images.pexels.com/photos/5206916/pexels-photo-5206916.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  team:
    "https://images.unsplash.com/photo-1666886573440-16ac24f89e31?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  partners:
    "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  aboutMission:
    "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  community:
    "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  outreach:
    "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export const SLOGANS = ["Sharing is not caring"];

export const MOBILE_SCHEDULE = {
  locations: [
    "Community centers",
    "Harm reduction sites",
    "Shelters and drop-in centers",
    "Outreach events",
    "Safe consumption sites",
  ],
  contact: "Call for the current schedule and locations in your neighborhood.",
};

export const SERVICE_PILLARS = [
  {
    id: "telephone-intake",
    icon: "PhoneCall",
    title: "Telephone Intake",
    tagline: "24 Hours / 365 days a year",
    description:
      "Reach a real person any time. Our intake line connects you to screening, treatment and support whenever you need it.",
  },
  {
    id: "mobile-unit",
    icon: "Ambulance",
    title: "Mobile Unit",
    tagline: "Attend a local screening",
    description:
      "Mobile clinics bring on-site screening, bloodwork and telemedicine directly into your community.",
  },
  {
    id: "pharmacy",
    icon: "Cross",
    title: "Pharmacy",
    tagline: "A participating pharmacy near you",
    description:
      "Our pharmacy network offers a trusted, judgment-free entry point for screening, education and treatment.",
  },
];

export const SERVICES = [
  {
    id: "point-of-care-testing",
    icon: "Droplet",
    title: "Point of Care Testing",
    tagline: "Rapid results, on the spot",
    description:
      "Fast, on-site Hepatitis C and HIV testing — no waiting days and no need for a separate lab visit.",
  },
  {
    id: "treatment-coordination",
    icon: "Workflow",
    title: "Treatment Coordination",
    tagline: "Every step, handled",
    description:
      "We coordinate every stage of treatment — prescriptions, pharmacy, bloodwork and follow-up — so nothing falls through the cracks.",
  },
  {
    id: "case-management",
    icon: "UserCheck",
    title: "Case Management",
    tagline: "A dedicated coordinator",
    description:
      "A dedicated coordinator supports you through any barrier standing between you and a cure.",
  },
  {
    id: "patient-reward-program",
    icon: "Banknote",
    title: "Patient Reward Program",
    tagline: "Incentives that support you",
    description:
      "Cash incentives recognize and support you for staying engaged with screening, adherence reporting and follow-up appointments.",
  },
  {
    id: "harm-reduction-supplies",
    icon: "Syringe",
    title: "Harm Reduction Supplies",
    tagline: "Prevent HCV & HIV",
    description:
      "Free harm reduction supplies to help prevent the spread of Hepatitis C (HCV) and HIV in the communities we serve.",
  },
  {
    id: "naloxone-distribution",
    icon: "LifeBuoy",
    title: "Naloxone Distribution",
    tagline: "Overdose response, ready",
    description:
      "Providing naloxone to patients at risk of opioid overdose — a simple, life-saving tool placed directly in the community.",
  },
];

export const PROGRAMS = [
  {
    id: "education",
    icon: "BookOpen",
    title: "Education",
    description:
      "Comprehensive, plain-language Hepatitis C education tailored to be understood by every client we serve.",
  },
  {
    id: "screening",
    icon: "Search",
    title: "Screening",
    description:
      "Rapid Hepatitis C and HIV screening — including on-site bloodwork inside our mobile units — with no barriers to entry.",
  },
  {
    id: "diagnosis",
    icon: "Stethoscope",
    title: "Diagnosis",
    description:
      "Expedited access to diagnosis so no one waits in uncertainty — fast confirmatory testing and clear next steps.",
  },
  {
    id: "treatment",
    icon: "HeartPulse",
    title: "Treatment",
    description:
      "Linkage to treatment — including same-day access for qualified patients. Hep C treatment is 97% effective.",
  },
  {
    id: "wraparound",
    icon: "HandHeart",
    title: "Wraparound Support",
    description:
      "Transportation, appointment reminders, harm-reduction education, reimbursement help, adherence support and more.",
  },
  {
    id: "harm-reduction-supplies",
    icon: "Syringe",
    title: "Harm Reduction Supplies",
    description:
      "We provide harm reduction supplies to help prevent the spread of Hepatitis C (HCV) and HIV in the communities we serve.",
  },
  {
    id: "mobile-clinics",
    icon: "Ambulance",
    title: "Mobile Clinics",
    description:
      "Mobile clinics deliver on-site screening and bloodwork to shelters and marginalized care settings.",
  },
  {
    id: "pharmacy-program",
    icon: "Cross",
    title: "Pharmacy Program",
    description:
      "Community pharmacies — especially those offering methadone services — act as trusted entry points for care.",
  },
  {
    id: "same-day",
    icon: "Zap",
    title: "Same-Day Treatment",
    description:
      "Streamlined access for patients lost to follow-up, coordinating care and counselling in real time.",
  },
  {
    id: "telehealth",
    icon: "Video",
    title: "Telehealth",
    description:
      "24/7 phone and virtual support for treatment questions, adherence and connection to additional services.",
  },
  {
    id: "registry",
    icon: "ClipboardList",
    title: "HepCURE CRM",
    description:
      "A proprietary platform tracking the patient journey, clinical markers and barriers — enabling real-time collaboration.",
  },
];

export const STATS = [
  { value: "20,000+", label: "People screened for Hepatitis C" },
  { value: "5,000+", label: "Diagnosed and referred for treatment" },
  { value: "70,000+", label: "Hours of dedicated support" },
  { value: "$1,000,000+", label: "Invested in patient support" },
];

export const ABOUT_VALUES = [
  {
    icon: "Users",
    title: "We go to the people",
    description:
      "Instead of asking at-risk people to seek out treatment, we design programs completely around the patient and meet them in their communities.",
  },
  {
    icon: "ShieldCheck",
    title: "We eliminate stigma",
    description:
      "Our programming is built specifically to remove the stigma so often present in traditional healthcare settings.",
  },
  {
    icon: "Globe",
    title: "We think in 2030",
    description:
      "Everything we do supports the World Health Organization's goal of eliminating Hepatitis C as a public health threat by 2030.",
  },
];

export const TEAM = [
  {
    name: "Dr. A. Mensah",
    role: "Medical Director",
    image:
      "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    name: "J. Okafor, RN",
    role: "Lead Outreach Nurse",
    image:
      "https://images.unsplash.com/photo-1594824476967-48c8b964273f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    name: "M. Delgado",
    role: "Peer Support Coordinator",
    image:
      "https://images.unsplash.com/photo-1622253692010-333f2da6031d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    name: "S. Nguyen, PharmD",
    role: "Pharmacy Network Lead",
    image:
      "https://images.unsplash.com/photo-1537368910025-700350fe46c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
];

export const PARTNER_CATEGORIES = [
  {
    icon: "Home",
    title: "Community Care Settings",
    count: "150+",
    description:
      "Homeless shelters and marginalized care settings across Ontario supported by our mobile units.",
  },
  {
    icon: "Cross",
    title: "Pharmacy Partners",
    count: "20+",
    description:
      "Community pharmacies acting as entry points for screening, education and treatment.",
  },
  {
    icon: "Handshake",
    title: "Industry Collaboration",
    count: "5+",
    description:
      "Partnering with leading industry stakeholders to expand access without duplicating expensive care pathways.",
  },
  {
    icon: "BadgeCheck",
    title: "Service Ontario",
    count: "Authorized",
    description:
      "An authorized care partner helping clients replace lost or stolen health cards.",
  },
];

export const FAQS = [
  {
    q: "Who is HepCURE for?",
    a: "HepCURE is designed for people most at risk of contracting Hepatitis C — particularly those affected by unsafe drug use and marginalized populations who face barriers in traditional healthcare settings. Everyone is welcome, without judgment.",
  },
  {
    q: "How much does it cost?",
    a: "HepCURE is a non-profit committed to curing marginalized people of Hepatitis C. We work to remove financial barriers to screening and treatment, including transportation and treatment reimbursement assistance.",
  },
  {
    q: "Is Hepatitis C actually curable?",
    a: "Yes. Modern Hepatitis C treatment is 97% effective. With the right support to start and stay on treatment, the vast majority of people are cured.",
  },
  {
    q: "Where do screenings happen?",
    a: "Screenings take place in convenient, trusted settings — our mobile clinics that travel to shelters and communities, and through our network of participating community pharmacies.",
  },
  {
    q: "What support is available during treatment?",
    a: "We provide wraparound support: transportation, appointment and text reminders, harm-reduction education, medication adherence support, replacement of lost medication, health card replacement help, and 24/7 telehealth.",
  },
  {
    q: "How can my organization partner with HepCURE?",
    a: "We collaborate with like-minded organizations to expand our programming without duplicating expensive care pathways. Reach out through our contact page to start a conversation.",
  },
];


export const LEGAL = {
  terms: {
    id: "terms",
    label: "Terms of Use",
    sections: [
      {
        heading: "1. Acceptance of Terms",
        paragraphs: [
          'Your access to and use of the HepCURE platform and website (the "Service") is subject to the following terms and conditions. By accessing, browsing, and using the Service, you accept, without limitation or qualification, these Terms of Use. If you do not agree to these terms, please do not use our Service.',
        ],
      },
      {
        heading: "2. Ownership and Restrictions of Use",
        paragraphs: [
          'This website and platform are owned and operated by HepCURE. Any and all content, data, graphics, photographs, images, audio, video, software, systems, processes, trademarks, service marks, trade names and other information including the "look and feel" of the Service (collectively, the "Content") are proprietary to HepCURE. The Content is protected by Canadian copyright laws and international copyright treaties.',
          "You may not modify, copy, reproduce, publish, post, transmit, distribute, display, perform, create derivative works from, transfer or sell any Content without express prior written consent. You may download, print and reproduce Content for your own non-commercial, informational purposes provided you maintain all copyright or proprietary notices.",
        ],
      },
      {
        heading: "3. User Conduct",
        paragraphs: [
          "You agree to access and use the Service only for lawful purposes. You agree that you will not:",
        ],
        bullets: [
          "Use the Service to commit a criminal offense or encourage unlawful conduct",
          "Post or transmit discriminatory, libelous, harassing, defamatory, obscene, or unlawful content",
          "Use the Service to impersonate other parties or entities",
          "Upload content containing malicious software or code that may damage the Service",
          "Attempt to gain unauthorized access to any systems or equipment",
          "Collect or store personal information about others without authorization",
          "Disrupt the normal flow of communication or data transfer",
        ],
      },
      {
        heading: "4. Restricted Access",
        paragraphs: [
          "Access to certain areas of our Service is restricted. If we provide you with credentials (such as a user ID and password) to enable access to restricted areas, you must ensure that credentials are kept confidential. If you have reason to believe your credentials have been compromised, you must inform us immediately.",
        ],
      },
      {
        heading: "5. Data Accuracy",
        paragraphs: [
          "It is the responsibility and obligation of all users to enter, update and maintain the most accurate and complete information for reference within the HepCURE system. You agree that any information provided will be complete and accurate.",
        ],
      },
      {
        heading: "6. Disclaimer of Warranties",
        paragraphs: [
          'The materials on our Service are provided "as is". HepCURE makes no warranties, expressed or implied, and hereby disclaims all other warranties, including without limitation, implied warranties of merchantability, fitness for a particular purpose, or non-infringement of intellectual property rights. We do not warrant that the Service will be uninterrupted, error-free, or free of viruses or other harmful components.',
        ],
      },
      {
        heading: "7. Limitation of Liability",
        paragraphs: [
          "In no event will HepCURE or its employees be liable for any incidental, indirect, special, punitive, exemplary, or consequential damages arising out of your use of or inability to use the Service, including without limitation loss of revenue, anticipated profits, goodwill, business, or data.",
        ],
      },
      {
        heading: "8. Termination",
        paragraphs: [
          "HepCURE may, in its sole discretion, terminate or suspend your access to the Service without notice for any reason, including violation of these Terms of Use or conduct believed to be unlawful or harmful to others.",
        ],
      },
      {
        heading: "9. Applicable Laws",
        paragraphs: [
          "These Terms of Use shall be governed by and construed in accordance with the laws of the Province of Ontario and the laws of Canada applicable therein. Any dispute shall be submitted to a court of competent jurisdiction in Ontario, Canada.",
        ],
      },
      {
        heading: "10. Modifications",
        paragraphs: [
          "HepCURE maintains the right to modify these Terms of Use at any time. Modifications are effective immediately upon posting. Your continued use of the Service following any modification signifies acceptance of such modification. You should periodically review these Terms of Use.",
        ],
      },
    ],
  },
  privacy: {
    id: "privacy",
    label: "Privacy Policy",
    sections: [
      {
        heading: "1. Introduction",
        paragraphs: [
          'HepCURE ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform and services. We comply with the Personal Information Protection and Electronic Documents Act (PIPEDA) in Canada.',
        ],
      },
      {
        heading: "2. Information We Collect",
        paragraphs: ["We may collect the following types of information:"],
        bullets: [
          "Personal Information: Name, email address, phone number, organization name, job title",
          "Client Data: Information you enter about the individuals you serve through our platform",
          "Usage Data: Information about how you access and use our Service",
          "Device Information: Browser type, IP address, device identifiers",
          "Cookies and Tracking: Data collected through cookies and similar technologies",
        ],
      },
      {
        heading: "3. How We Use Your Information",
        paragraphs: ["We use collected information to:"],
        bullets: [
          "Provide, operate, and maintain our Service",
          "Improve, personalize, and expand our Service",
          "Communicate with you about updates, support, and administrative messages",
          "Process transactions and send related information",
          "Detect, prevent, and address technical issues and security threats",
          "Comply with legal obligations",
        ],
      },
      {
        heading: "4. Data Protection and Security",
        paragraphs: [
          "We implement appropriate technical and organizational security measures to protect your personal information. All data is encrypted in transit and at rest. Access to personal data is restricted to authorized personnel only. We maintain audit logs of data access and regularly review our security practices.",
        ],
      },
      {
        heading: "5. Data Sharing and Disclosure",
        paragraphs: ["We do not sell your personal information. We may share information with:"],
        bullets: [
          "Service Providers: Third parties who perform services on our behalf",
          "Legal Requirements: When required by law or to protect our rights",
          "Business Transfers: In connection with a merger, acquisition, or sale of assets",
          "With Your Consent: When you have given explicit permission",
        ],
      },
      {
        heading: "6. Data Retention",
        paragraphs: [
          "We retain personal information only for as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce our agreements. Client data retention periods may vary based on your organization's requirements and applicable regulations.",
        ],
      },
      {
        heading: "7. Your Rights",
        paragraphs: ["Depending on your jurisdiction, you may have the right to:"],
        bullets: [
          "Access your personal information",
          "Correct inaccurate or incomplete information",
          "Request deletion of your personal information",
          "Object to or restrict processing of your information",
          "Data portability",
          "Withdraw consent at any time",
        ],
      },
      {
        heading: "8. Changes to This Policy",
        paragraphs: [
          'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.',
        ],
      },
      {
        heading: "9. Contact Us",
        paragraphs: [
          "If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:",
          "HepCURE\n530 Adelaide St. W., Unit 6107\nToronto, ON M5V 1T5\ninfo@hepcure.ca",
        ],
      },
    ],
  },
  security: {
    id: "security",
    label: "Security",
    sections: [
      {
        heading: "Our Commitment to Security",
        paragraphs: [
          "At HepCURE, protecting your data is our highest priority. We implement industry-leading security measures to ensure your information remains safe from unauthorized access and threats.",
        ],
      },
      {
        heading: "Regular Penetration Testing",
        paragraphs: [
          "We conduct regular penetration testing and security audits performed by independent third-party security experts. These assessments help us identify and address potential vulnerabilities before they can be exploited.",
        ],
      },
      {
        heading: "Security Audits",
        paragraphs: [
          "Our systems undergo comprehensive security audits to ensure compliance with industry standards and best practices. We continuously monitor and improve our security posture based on audit findings and emerging threats.",
        ],
      },
      {
        heading: "Data Protection Measures",
        bullets: [
          "End-to-end encryption for all data",
          "Multi-factor authentication (MFA)",
          "Role-based access controls",
          "Automated backups with off-site storage",
          "24/7 monitoring and intrusion detection",
          "Incident response and disaster recovery",
        ],
      },
      {
        heading: "Compliance",
        paragraphs: [
          "We maintain compliance with applicable data protection regulations including PIPEDA (Canada). Our security practices are designed to meet or exceed the requirements of these frameworks.",
        ],
      },
      {
        heading: "Report a Vulnerability",
        paragraphs: [
          "If you discover a potential security vulnerability, please contact us immediately at info@hepcure.ca. We take all reports seriously and will investigate promptly.",
        ],
      },
    ],
  },
};
